import type {
  QueryDetailAbsoluteDatesInput,
  QueryDetailGeneralInput,
} from '../../models/api/query/query-list';

/** General-parameter date fields shown beside legacy schedule increment checkboxes. */
export type QueryGeneralDateField =
  | 'asOfDate'
  | 'lossDateFrom'
  | 'lossDateThrough'
  | 'openedDateFrom'
  | 'openedDateThrough'
  | 'closedDateFrom'
  | 'closedDateThrough'
  | 'reopenedDateFrom'
  | 'reopenedDateThrough'
  | 'reportedToClientFrom'
  | 'reportedToClientThrough'
  | 'reportedToCmsFrom'
  | 'reportedToCmsThrough'
  | 'lastUpdatedFrom'
  | 'lastUpdatedThrough'
  | 'lastFinancialUpdateFrom'
  | 'lastFinancialUpdateThrough'
  | 'paymentDateFrom'
  | 'paymentDateThrough';

export type QueryScheduleIncrementRow = {
  label: string;
  generalField: QueryGeneralDateField;
  scheduleControl: string;
};

/** Legacy QueryWizardSchedule.aspx: Literal next to each increment checkbox. */
export function formatScheduleGeneralDateParen(
  date: Date | null | undefined,
): string {
  if (date == null) {
    return ' (blank)';
  }
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) {
    return ' (blank)';
  }
  const month = d.getMonth() + 1;
  const day = `${d.getDate()}`.padStart(2, '0');
  return ` (${month}/${day}/${d.getFullYear()})`;
}

/** Matches legacy ScheduleQueryPageController CreateDatesElement order. */
export const QUERY_SCHEDULE_INCREMENT_FROM_ROWS: QueryScheduleIncrementRow[] = [
  { label: 'Loss Date', generalField: 'lossDateFrom', scheduleControl: 'lossDateFrom' },
  { label: 'Claim Opened', generalField: 'openedDateFrom', scheduleControl: 'claimOpenedFrom' },
  { label: 'Claim Closed', generalField: 'closedDateFrom', scheduleControl: 'claimClosedFrom' },
  { label: 'Claim Reopened', generalField: 'reopenedDateFrom', scheduleControl: 'claimReopenedFrom' },
  {
    label: 'Reported to Client',
    generalField: 'reportedToClientFrom',
    scheduleControl: 'reportedToClientFrom',
  },
  {
    label: 'Reported to SCMS',
    generalField: 'reportedToCmsFrom',
    scheduleControl: 'reportedToCmsFrom',
  },
  { label: 'Last Update', generalField: 'lastUpdatedFrom', scheduleControl: 'lastUpdatedFrom' },
  {
    label: 'Last Financial Update',
    generalField: 'lastFinancialUpdateFrom',
    scheduleControl: 'lastFinancialUpdateFrom',
  },
  { label: 'Payment Date', generalField: 'paymentDateFrom', scheduleControl: 'paymentDateFrom' },
];

/** Form values for General date fields (schedule hints + edit load). */
export type QueryGeneralDateFormPatch = {
  asOfDate: Date | null;
  lossDateFrom: Date | null;
  lossDateThrough: Date | null;
  openedDateFrom: Date | null;
  openedDateThrough: Date | null;
  closedDateFrom: Date | null;
  closedDateThrough: Date | null;
  reopenedDateFrom: Date | null;
  reopenedDateThrough: Date | null;
  reportedToClientFrom: Date | null;
  reportedToClientThrough: Date | null;
  reportedToCmsFrom: Date | null;
  reportedToCmsThrough: Date | null;
  lastUpdatedFrom: Date | null;
  lastUpdatedThrough: Date | null;
  lastFinancialUpdateFrom: Date | null;
  lastFinancialUpdateThrough: Date | null;
  paymentDateFrom: Date | null;
  paymentDateThrough: Date | null;
};

function parseQueryDetailDate(value: string | null | undefined): Date | null {
  if (value == null || String(value).trim() === '') {
    return null;
  }
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

function patchAbsoluteDates(abs: QueryDetailAbsoluteDatesInput | null | undefined): Omit<
  QueryGeneralDateFormPatch,
  'asOfDate'
> {
  return {
    lossDateFrom: parseQueryDetailDate(abs?.lossDateFrom),
    lossDateThrough: parseQueryDetailDate(abs?.lossDateThrough),
    openedDateFrom: parseQueryDetailDate(abs?.openedDateFrom),
    openedDateThrough: parseQueryDetailDate(abs?.openedDateThrough),
    closedDateFrom: parseQueryDetailDate(abs?.closedDateFrom),
    closedDateThrough: parseQueryDetailDate(abs?.closedDateThrough),
    reopenedDateFrom: parseQueryDetailDate(abs?.reopenedDateFrom),
    reopenedDateThrough: parseQueryDetailDate(abs?.reopenedDateThrough),
    reportedToClientFrom: parseQueryDetailDate(abs?.reportedToClientFrom),
    reportedToClientThrough: parseQueryDetailDate(abs?.reportedToClientThrough),
    reportedToCmsFrom: parseQueryDetailDate(abs?.reportedToCmsFrom),
    reportedToCmsThrough: parseQueryDetailDate(abs?.reportedToCmsThrough),
    lastUpdatedFrom: parseQueryDetailDate(abs?.lastUpdatedFrom),
    lastUpdatedThrough: parseQueryDetailDate(abs?.lastUpdatedThrough),
    lastFinancialUpdateFrom: parseQueryDetailDate(abs?.lastFinancialUpdateFrom),
    lastFinancialUpdateThrough: parseQueryDetailDate(abs?.lastFinancialUpdateThrough),
    paymentDateFrom: parseQueryDetailDate(abs?.paymentDateFrom),
    paymentDateThrough: parseQueryDetailDate(abs?.paymentDateThrough),
  };
}

/** Hydrate query form date controls from GET query detail (legacy schedule page literals). */
export function generalDateFieldsFromQueryDetail(
  general: QueryDetailGeneralInput | undefined,
): QueryGeneralDateFormPatch {
  return {
    asOfDate: parseQueryDetailDate(general?.asOfDate),
    ...patchAbsoluteDates(general?.absoluteDates),
  };
}

export const QUERY_SCHEDULE_INCREMENT_THROUGH_ROWS: QueryScheduleIncrementRow[] = [
  { label: 'Loss Date', generalField: 'lossDateThrough', scheduleControl: 'lossDateThrough' },
  {
    label: 'Claim Opened',
    generalField: 'openedDateThrough',
    scheduleControl: 'claimOpenedThrough',
  },
  {
    label: 'Claim Closed',
    generalField: 'closedDateThrough',
    scheduleControl: 'claimClosedThrough',
  },
  {
    label: 'Claim Reopened',
    generalField: 'reopenedDateThrough',
    scheduleControl: 'claimReopenedThrough',
  },
  {
    label: 'Reported to Client',
    generalField: 'reportedToClientThrough',
    scheduleControl: 'reportedToClientThrough',
  },
  {
    label: 'Reported to SCMS',
    generalField: 'reportedToCmsThrough',
    scheduleControl: 'reportedToCmsThrough',
  },
  {
    label: 'Last Update',
    generalField: 'lastUpdatedThrough',
    scheduleControl: 'lastUpdatedThrough',
  },
  {
    label: 'Last Financial Update',
    generalField: 'lastFinancialUpdateThrough',
    scheduleControl: 'lastFinancialUpdateThrough',
  },
  {
    label: 'Payment Date',
    generalField: 'paymentDateThrough',
    scheduleControl: 'paymentDateThrough',
  },
];
